import chroma from 'chroma-js';
import { isColor } from '../utils/colorUtils';
import { isNumber } from '../utils/types';
import { AbstractFormatter } from '../Formatter';
import { TypedValue } from '../DataPrimitive';
import { DataSeries } from '../DataSeries';
import { DataPoint } from '../DataPoint';
import { DataFrame } from '../DataFrame';
import EncodingExecutor from '../EncodingExecutor';

export const DEFAULT_MIN_COLOR = 'rgba(123,86,219,0.4)';
export const DEFAULT_MAX_COLOR = 'rgba(123,86,219,1)';

export interface GradientConfig {
    stops?: number[];
    colors: string[];
}

/**
 * Based on stops and colors, this formatter maps the value to an interpolated color.
 *
 * The `config` follows this criteria: `{ stops: [0, 100, 230], colors: ['red', 'green', 'blue'] }`
 *
 * If no config is specified, the default `colors` are `['rgba(123,86,219,0.4)', 'rgba(123,86,219,1)']`. The default `stops` are dependent on the DataSeries provided.
 *
 * Given a DataSeries with n values, the stops would consist of [min, ...(configured stops),  max] if stops values are contained in [min, max] - recommended.
 * And min/max map to 'rgba(123,86,219,0.4)'/'rgba(123,86,219,1)', respectively.
 *
 * For example, if 0 < value < 100, the corresponding interpolated color will be between red and green.
 *
 * ```js
 * <SampleViz
 *     context={{
 *         gradientConfig: { stops: [50, 100, 150], colors: ['red', 'green', 'blue'] }
 *     }}
 *     options={{
 *         colorOption: '> primary | seriesByIndex(0) | gradient(gradientConfig)' // it maps [0, 50, 230] to ['#7B56DB66', '#FF0000', '#7B56DB']
 *         colorOption1: '> primary | seriesByIndex(1) | gradient(gradientConfig)' // it maps [50, 100, 150] to ['red', 'green', 'blue']
 *     }}
 *     dataSources={{
 *         data: {
 *             primary: {
 *                 columns: [[0, 50, 230], [50, 100, 150]],
 *                 fields: [{ name: 'foo' }, { name: 'bar' }]
 *             }
 *         }
 *     }}
 * />
 * ```
 *
 * DataFrame (table) usage:
 * When the input is a DataFrame (for example, a table), the formatter computes a global min/max across
 * all numeric series in the frame. Those min/max are then used to build stops, and the formatter returns
 * a new DataFrame of type `'color'` with each numeric cell mapped to its interpolated color.
 *
 * Minimal DSL example with a DataFrame in context as `table`:
 *
 * ```js
 * <SampleViz
 *     context={{
 *         gradientConfig: { stops: [50, 100, 150], colors: ['red', 'green', 'blue'] }
 *     }}
 *     options={{
 *         colorOption: '> table | gradient(gradientConfig)'
 *     }}
 *     dataSources={{
 *         data: {
 *             primary: {
 *                 columns: [[0, 50, 230], [50, 100, 150]],
 *                 fields: [{ name: 'foo' }, { name: 'bar' }]
 *             }
 *         }
 *     }}
 * />
 * ```
 *
 * If no `stops` are provided, they are derived from the global DataFrame min/max.
 *
 * @extends AbstractFormatter<DataType, DataType>
 */
export class Gradient extends AbstractFormatter<'number', 'color'> {
    readonly config: GradientConfig;

    readonly colors: string[];

    readonly stops: number[];

    constructor(config?: GradientConfig) {
        super();
        this.config = EncodingExecutor.rawTree(config) || {};

        this.colors = this.config.colors || [];
        this.stops = this.config.stops || [];
        if (!Array.isArray(this.colors) || !Array.isArray(this.stops)) {
            throw new Error("Gradient config: both 'colors' and 'stops' in should be 1D array.");
        }
        this.colors.forEach(c => {
            if (!isColor(c)) {
                throw new Error("Gradient config: every element in 'colors' should be a color.");
            }
        });
        Gradient.sortArray(this.stops);
    }

    static readonly sortArray = (array: number[]): void => {
        array.sort((a, b) => a - b);
    };

    /**
     * Computes numeric min and max over a DataSeries by coercing each point's value
     * to a number. This avoids lexicographic ordering from DataSeries.min()/max(),
     * which compares coercedValue strings (e.g. '10' < '2') for string-typed points.
     * Returns { min: NaN, max: NaN } if no numeric values are found.
     */
    private static numericMinMax(series: DataSeries): { min: number; max: number } {
        let min = Number.POSITIVE_INFINITY;
        let max = Number.NEGATIVE_INFINITY;
        series.points.forEach(p => {
            const raw = p.getValue().value;
            if (isNumber(raw)) {
                const n = Number(raw);
                if (n < min) min = n;
                if (n > max) max = n;
            }
        });
        return {
            min: Number.isFinite(min) ? min : NaN,
            max: Number.isFinite(max) ? max : NaN,
        };
    }

    static readonly addDefaultEndingColors = (colors: string[], n: number): void => {
        // add n ending purples to colors
        for (let i = 0; i < n; i += 1) {
            colors.push(DEFAULT_MAX_COLOR);
        }
    };

    static readonly ignoreMinMaxWhenNeeded = (
        min: number,
        max: number,
        stops: number[],
        newStops: number[],
        newColors: string[]
    ): void => {
        if (min >= stops[0]) {
            // ignore "min" stop and color
            newStops.shift();
            newColors.shift();
        }
        if (max <= stops[stops.length - 1]) {
            // ignore "max" stop and color
            newStops.pop();
            newColors.pop();
        }
    };

    private prepareStopsAndColors(min: number, max: number) {
        const colorsLength = this.colors.length;
        const stopsLength = this.stops.length;

        // No stops configured
        if (stopsLength === 0) {
            return Gradient.computeNoStopsCase(min, max, this.colors);
        }

        // Stops configured
        const newStops = [min, ...this.stops, max];
        if (colorsLength === 0) {
            const newColors = Gradient.computeColorsForStopsOnly(stopsLength);
            Gradient.ignoreMinMaxWhenNeeded(min, max, this.stops, newStops, newColors);
            return { newStops, newColors };
        }

        if (stopsLength + 2 >= colorsLength) {
            return Gradient.alignColorsWithStops(
                min,
                max,
                this.stops,
                newStops,
                this.colors,
                stopsLength,
                colorsLength
            );
        }

        throw new Error('Gradient config: too many colors set for the given stops.');
    }

    private static computeNoStopsCase(min: number, max: number, colors: string[]) {
        const colorsLength = colors.length;
        const newColors = [DEFAULT_MIN_COLOR, DEFAULT_MAX_COLOR];
        if (colorsLength > 2) {
            const newStops: number[] = [];
            for (let i = 0; i < colorsLength; i += 1) {
                newStops.push(min + ((max - min) * i) / (colorsLength - 1));
            }
            return { newStops, newColors: colors };
        }
        colors.forEach((c, i) => {
            newColors[i] = c;
        });
        return { newStops: [min, max], newColors };
    }

    private static computeColorsForStopsOnly(stopsLength: number): string[] {
        if (stopsLength === 1) {
            return [DEFAULT_MIN_COLOR, DEFAULT_MAX_COLOR, DEFAULT_MAX_COLOR];
        }
        if (stopsLength === 2) {
            return [DEFAULT_MIN_COLOR, DEFAULT_MIN_COLOR, DEFAULT_MAX_COLOR, DEFAULT_MAX_COLOR];
        }
        throw new Error('Gradient config: provide at most two stops when colors is empty.');
    }

    private static alignColorsWithStops(
        min: number,
        max: number,
        stops: number[],
        newStops: number[],
        colors: string[],
        stopsLength: number,
        colorsLength: number
    ) {
        const tmpColors = [...colors];
        if (stopsLength >= colorsLength) {
            tmpColors.unshift(DEFAULT_MIN_COLOR);
        }
        Gradient.addDefaultEndingColors(tmpColors, stopsLength + 2 - tmpColors.length);
        Gradient.ignoreMinMaxWhenNeeded(min, max, stops, newStops, tmpColors);
        if (newStops.length !== tmpColors.length) {
            throw new Error('Gradient config: not equal length of stops and colors.');
        }
        return { newStops, newColors: tmpColors };
    }

    private static colorForValue(value: number, newStops: number[], newColors: string[]): string {
        const lastStop = newStops[newStops.length - 1];
        const firstStop = newStops[0];

        if (value > lastStop || value < firstStop) {
            const index = value > lastStop ? newColors.length - 1 : 0;
            return chroma(newColors[index]).hex().toUpperCase();
        }

        if (newStops.some(s => Number.isNaN(Number(s)))) {
            // eslint-disable-next-line no-console
            console.warn(
                'Could not format gradient color: stops are required to be numbers. Falling back to first color.'
            );
            return chroma(newColors[0]).hex().toUpperCase() || '#000';
        }

        let idx = newStops.findIndex(s => value <= s);
        if (idx <= 0) {
            // Align with previous loop behavior: use the first segment [stops[0], stops[1]]
            idx = Math.min(1, newStops.length - 1);
        }

        const upperVal = newStops[idx];
        const lowerVal = newStops[idx - 1];
        let tau = 0;
        if (upperVal === lowerVal) {
            tau = value === 0 ? 0 : 1;
        } else {
            tau = (value - lowerVal) / (upperVal - lowerVal);
        }
        return chroma
            .scale([newColors[idx - 1], newColors[idx]])(tau)
            .hex()
            .toUpperCase();
    }

    // Override to support DataFrame-wide min/max when gradient is applied to a table (DataFrame)
    format(dataPrimitive: any): any {
        if (dataPrimitive instanceof DataFrame) {
            // compute global min/max across all numeric series
            let globalMin = Number.POSITIVE_INFINITY;
            let globalMax = Number.NEGATIVE_INFINITY;
            dataPrimitive.series.forEach((series: DataSeries) => {
                if (series?.points?.length) {
                    const { min: sMin, max: sMax } = Gradient.numericMinMax(series);
                    if (!Number.isNaN(sMin)) {
                        globalMin = Math.min(globalMin, sMin);
                    }
                    if (!Number.isNaN(sMax)) {
                        globalMax = Math.max(globalMax, sMax);
                    }
                }
            });

            if (!Number.isFinite(globalMin) || !Number.isFinite(globalMax)) {
                return super.format(dataPrimitive);
            }

            const { newStops, newColors } = this.prepareStopsAndColors(globalMin, globalMax);

            const mappedSeries: DataSeries<'color'>[] = dataPrimitive.series.map((series: DataSeries) => {
                const outPoints = series.points.map(p => {
                    const input = p.getValue();
                    const isNumeric =
                        input.type === 'number' || (input.type === 'string' && isNumber(input.value));
                    if (!isNumeric) {
                        return new DataPoint(p.field, { type: 'color', value: undefined }, p.isSearchResult);
                    }
                    const color = Gradient.colorForValue(
                        Number(input.value) as unknown as number,
                        newStops,
                        newColors
                    );
                    return new DataPoint(p.field, { type: 'color', value: color }, p.isSearchResult);
                });
                return new DataSeries<'color'>(outPoints);
            });

            return new DataFrame<'color'>(mappedSeries);
        }
        return super.format(dataPrimitive);
    }

    private prepareColorsAndStopsSeries(series: DataSeries) {
        const { min, max } = Gradient.numericMinMax(series);
        return this.prepareStopsAndColors(min, max);
    }

    private prepareColorsAndStopsPoint(value: number) {
        let newStops = this.stops;
        let newColors = this.colors;
        if (this.stops.length === 0) {
            newStops = [0, 1];
        }
        if (this.colors.length === 0) {
            newColors = [DEFAULT_MIN_COLOR, DEFAULT_MAX_COLOR];
        }
        if (this.stops.length === this.colors.length && this.stops.length === 1 && value < this.stops[0]) {
            newColors = [DEFAULT_MIN_COLOR, ...this.colors];
        }
        return { newStops, newColors };
    }

    protected formatTypedValue(p: DataPoint<'number'>, series: DataSeries): TypedValue<'color'> {
        const input: TypedValue<'number'> = p.getValue();
        const { type, value } = input;
        // accept string-typed numeric values (e.g. '500' from a str-typed SPL field)
        const isNumericValue = type === 'number' || (type === 'string' && isNumber(value));
        if (!isNumericValue) {
            return { type: 'color', value: undefined };
        }

        const numericValue = Number(value);
        const isDataSeries = !!series?.points?.length;
        const { newStops, newColors } = isDataSeries
            ? this.prepareColorsAndStopsSeries(series)
            : this.prepareColorsAndStopsPoint(numericValue);

        const interpolateColor = Gradient.colorForValue(numericValue, newStops, newColors);
        return { type: 'color', value: interpolateColor };
    }
}
